import { useQuery } from '@tanstack/react-query';
import { ArrowRight, CalendarDays, Shuffle, Trophy, Users } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useSelectedClub } from '@/features/club';
import {
  Bracket,
  BracketMatchCard,
  buildGroupTable,
  buildNameMap,
  buildProfileNames,
  buildSeedMap,
  championLabel,
  classificationGroupsView,
  COMPETITION_SYSTEM_LABELS,
  collectMemberIds,
  countPlayingSlots,
  extraQualifierCaptains,
  finalGroupView,
  finalPhaseState,
  DrawSuspenseOverlay,
  formatDateRange,
  GroupStandingsCard,
  matchHasPair,
  groupsView,
  MatchScoreRow,
  podiumEntries,
  PodiumCard,
  roundRobinRounds,
  SECOND_PHASE_LABELS,
  singleGroupRoster,
  toBracketMatchView,
  toBracketView,
  unresolvedGroupMatches,
  useAdvanceMutation,
  useBracketQuery,
  useCompetitionMatchesQuery,
  useCompetitionQuery,
  useDrawSuspense,
  useParticipantsQuery,
  useStandingsQuery,
  WinnerCard,
  type BracketMatch,
} from '@/features/competition';
import { getUsersByIds, userKeys } from '@/features/user';
import { ApiError } from '@/shared/api/ApiError';
import { Alert, Button, Card, CardHeader, MetaBar, Skeleton, useToast } from '@/shared/ui';

interface TournamentProgressPageProps {
  /** Widok fazy finałowej GROUP_PLAYOFF (zakładka „Faza finałowa"). */
  finalPhase?: boolean;
}

/**
 * Zakładka przebiegu turnieju — wynik losowania i aktualny stan gry:
 * drabinka z kartą zwycięzcy (PLAYOFF), tabela ligi z terminarzem (SINGLE_GROUP)
 * albo — dla GROUP_PLAYOFF — dwa osobne widoki: faza grupowa (tabele grup
 * i terminarz) oraz faza finałowa (losowanie awansu, drabinka lub grupa finałowa).
 */
export function TournamentProgressPage({ finalPhase = false }: TournamentProgressPageProps) {
  const { t } = useTranslation('tournament');
  const { competitionId = '' } = useParams<{ competitionId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { clubId } = useSelectedClub();

  const competitionQuery = useCompetitionQuery(competitionId);
  const participantsQuery = useParticipantsQuery(competitionId);
  const competition = competitionQuery.data;

  const drawn =
    competition !== undefined &&
    (competition.status === 'IN_PROGRESS' || competition.status === 'COMPLETED');

  const bracketQuery = useBracketQuery(competitionId, drawn && competition.bracketSize !== null);
  const matchesQuery = useCompetitionMatchesQuery(competitionId, drawn);
  const matches = useMemo(() => matchesQuery.data ?? [], [matchesQuery.data]);
  const standingsQuery = useStandingsQuery(competitionId, drawn);
  const standings = useMemo(() => standingsQuery.data ?? [], [standingsQuery.data]);

  const advanceMutation = useAdvanceMutation(competitionId, clubId);

  /** Inscenizacja „Losowanie w toku” — wspólna z losowaniem głównym. */
  const advanceSuspense = useDrawSuspense();

  const participants = useMemo(() => participantsQuery.data ?? [], [participantsQuery.data]);
  const memberIds = useMemo(() => collectMemberIds(participants).sort(), [participants]);
  const profilesQuery = useQuery({
    queryKey: [...userKeys.all, 'batch', memberIds.join(',')],
    queryFn: () => getUsersByIds(memberIds),
    enabled: memberIds.length > 0,
  });
  const shortNames = useMemo(() => buildNameMap(profilesQuery.data ?? []), [profilesQuery.data]);
  const fullNames = useMemo(
    () => buildProfileNames(profilesQuery.data ?? []),
    [profilesQuery.data],
  );
  const seeds = useMemo(() => buildSeedMap(participants), [participants]);

  const bracketView = useMemo(
    () => (bracketQuery.data ? toBracketView(bracketQuery.data, shortNames, seeds) : null),
    [bracketQuery.data, shortNames, seeds],
  );
  const groups = useMemo(
    () => groupsView(participants, matches, fullNames),
    [participants, matches, fullNames],
  );
  // Aktualni posiadacze dodatkowych miejsc awansu („awans z dalszych miejsc") —
  // projekcja na żywo tej samej selekcji, którą wykona backend przy zamknięciu grup.
  const extraCaptains = useMemo(
    () =>
      extraQualifierCaptains(
        standings,
        competition?.advancingPerGroup ?? null,
        competition?.extraQualifiers ?? null,
      ),
    [standings, competition?.advancingPerGroup, competition?.extraQualifiers],
  );
  const finalGroup = useMemo(() => finalGroupView(matches, fullNames), [matches, fullNames]);
  // Grupy klasyfikacyjne drugiej fazy (kubełek 0 = podium, ujemne = niższe miejsca).
  const classificationGroups = useMemo(
    () => classificationGroupsView(matches, fullNames),
    [matches, fullNames],
  );
  // Podium turnieju (miejsca 1-3) — pojawia się od razu po rozstrzygnięciu
  // wszystkich decydujących meczów, bez czekania na zakończenie rozgrywki.
  const podium = useMemo(
    () => podiumEntries(bracketView, standings, matches, fullNames),
    [bracketView, standings, matches, fullNames],
  );
  const singleGroupRounds = useMemo(
    () => roundRobinRounds(matches, null, shortNames, seeds),
    [matches, shortNames, seeds],
  );
  const finalGroupRounds = useMemo(
    () => roundRobinRounds(matches, 0, shortNames, seeds),
    [matches, shortNames, seeds],
  );

  // Skład tabeli SINGLE_GROUP bierzemy z meczów, nie ze zgłoszeń — inaczej
  // do tabeli trafiłaby lista rezerwowa, która w losowaniu nie brała udziału.
  const singleGroupMembers = useMemo(
    () => singleGroupRoster(matches, fullNames),
    [matches, fullNames],
  );

  if (competitionQuery.isError) {
    return <Alert tone="danger">{t('draw.loadError')}</Alert>;
  }
  if (!competition) {
    return <Skeleton className="h-96 w-full" />;
  }

  if (!drawn) {
    return (
      <Alert tone="info">
        <span className="flex flex-wrap items-center gap-3">
          {t('progress.notDrawn')}
          <Button
            size="sm"
            leadingIcon={<Shuffle className="size-4" />}
            onClick={() => void navigate(`/turnieje/${competitionId}/losowanie`)}
          >
            {t('infoTab.goToDraw')}
          </Button>
        </span>
      </Alert>
    );
  }

  const kind =
    competition.system === 'PLAYOFF'
      ? ('bracket' as const)
      : competition.system === 'GROUP_PLAYOFF'
        ? ('groups' as const)
        : ('round-robin' as const);

  // Zakładka „Faza finałowa" istnieje tylko w systemie dwufazowym.
  if (finalPhase && kind !== 'groups') {
    return <Navigate to={`/turnieje/${competitionId}/drabinka`} replace />;
  }
  // Obsada turnieju bez rezerwy — ta sama liczba, którą widzi losowanie.
  const playing = countPlayingSlots(participants, competition.maxPlayers);
  const secondPhaseLabel = competition.secondPhaseType
    ? SECOND_PHASE_LABELS[competition.secondPhaseType]
    : SECOND_PHASE_LABELS.PLAYOFF;
  const phase = finalPhaseState(competition, matches);
  const unresolved = unresolvedGroupMatches(matches);
  const champion = bracketView ? championLabel(bracketView) : null;
  const matchesPending = matchesQuery.isPending;

  /** Komunikat błędu z API albo tekst zastępczy. */
  function describe(error: unknown, fallback: string): string {
    return error instanceof ApiError ? error.message : fallback;
  }

  function handleAdvance() {
    advanceSuspense.begin();

    advanceMutation.mutate(undefined, {
      // Wynik pokazujemy dopiero po dograniu paska — nakładka przykrywa
      // odświeżoną drabinkę, więc losowanie fazy finałowej też ma swój moment.
      onSuccess: () =>
        advanceSuspense.finishAfterMinimum(() =>
          showToast({ tone: 'success', description: t('draw.toast.advanced') }),
        ),
      onError: (error) => {
        advanceSuspense.abort();
        showToast({ tone: 'danger', description: describe(error, t('draw.toast.advanceFailed')) });
      },
    });
  }

  /** Otwiera ekran wyniku; karty wolnych losów (id `bye-…`) nie mają meczu. */
  function openMatch(match: BracketMatch) {
    if (match.id.startsWith('bye-') || match.id.startsWith('preview-')) return;
    void navigate(`/turnieje/${competitionId}/mecze/${match.id}/wynik`);
  }

  const bracketSection = (title: string) =>
    competition.bracketSize === null ? (
      <Alert tone="warning">{t('draw.result.noDraw')}</Alert>
    ) : bracketQuery.isPending ? (
      <Skeleton className="h-80 w-full" />
    ) : bracketView ? (
      <Card padding="lg" className="flex flex-col gap-4">
        <CardHeader title={title} className="mb-0" />
        <Bracket
          rounds={bracketView.rounds}
          showAvatars
          onMatchSelect={openMatch}
          finaleCompact={!champion}
          finale={
            <WinnerCard
              title={t('draw.result.champion')}
              {...(champion ? { winnerName: champion } : {})}
            />
          }
        />
        {bracketView.thirdPlace && (
          <div className="flex flex-col gap-2">
            <h3 className="text-[0.6875rem] font-bold tracking-[0.08em] text-fg-muted uppercase">
              {t('draw.result.thirdPlace')}
            </h3>
            {/* Para deblowa zajmuje dwie linie — karta potrzebuje wyższej ramki. */}
            <div
              className={
                matchHasPair(bracketView.thirdPlace) ? 'h-28 max-w-[15rem]' : 'h-16 max-w-[15rem]'
              }
            >
              <BracketMatchCard match={bracketView.thirdPlace} showAvatars onSelect={openMatch} />
            </div>
          </div>
        )}
        <p className="text-xs text-fg-subtle">{t('draw.result.legend')}</p>
      </Card>
    ) : null;

  return (
    <div className="flex flex-col gap-5">
      <MetaBar
        items={[
          {
            icon: <CalendarDays />,
            label: t('list.columns.term'),
            value: formatDateRange(competition.startDate, competition.endDate),
          },
          {
            icon: <Users />,
            label: t('draw.summary.players'),
            value: t('draw.summary.playersValue', {
              count: playing,
              max: competition.maxPlayers,
            }),
          },
          {
            icon: <Trophy />,
            label: t('draw.summary.system'),
            value: COMPETITION_SYSTEM_LABELS[competition.system] ?? competition.system,
          },
        ]}
      />

      {/* Podium po rozstrzygnięciu decydujących meczów — wspólne dla wszystkich systemów. */}
      {podium.length > 0 && <PodiumCard title={t('draw.result.podium')} entries={podium} />}

      {/* PLAYOFF — sama drabinka */}
      {kind === 'bracket' && bracketSection(t('draw.result.bracketTitle'))}

      {/* SINGLE_GROUP — terminarz każdy z każdym */}
      {kind === 'round-robin' &&
        (matchesPending ? (
          <Skeleton className="h-80 w-full" />
        ) : singleGroupRounds.length === 0 ? (
          <Alert tone="warning">{t('draw.result.noDraw')}</Alert>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Tabela ligi: punkty, W–P, bilans gemów i aktualne miejsca */}
            <GroupStandingsCard
              title={t('standings.title')}
              rows={buildGroupTable(null, singleGroupMembers, standings, fullNames, null)}
            />
            <div className="grid items-start gap-4 lg:grid-cols-2">
              {singleGroupRounds.map((round) => (
                <Card key={round.name} padding="md">
                  <CardHeader title={round.label} className="mb-2" />
                  <div className="flex flex-col divide-y divide-border">
                    {round.matches.map((match) => (
                      <button
                        key={match.id}
                        type="button"
                        onClick={() => openMatch(match)}
                        className="-mx-2 rounded-lg px-2 text-left transition-colors hover:bg-surface-hover"
                      >
                        <MatchScoreRow match={match} showAvatars />
                      </button>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}

      {/* GROUP_PLAYOFF — zakładka „Faza grupowa": tabele grup i terminarz */}
      {kind === 'groups' && !finalPhase && (
        <>
          {matchesPending ? (
            <Skeleton className="h-80 w-full" />
          ) : groups.length === 0 ? (
            <Alert tone="warning">{t('draw.result.noDraw')}</Alert>
          ) : (
            <div className="flex flex-col gap-4">
              <h2 className="text-lg font-bold text-fg-strong">{t('draw.result.groupsTitle')}</h2>
              <div className="grid gap-4 min-[1920px]:grid-cols-3 xl:grid-cols-2">
                {groups.map((group) => (
                  <GroupStandingsCard
                    key={group.number}
                    title={t('draw.result.group', { number: group.number })}
                    rows={buildGroupTable(
                      group.number,
                      group.members,
                      standings,
                      fullNames,
                      competition.advancingPerGroup ?? null,
                      extraCaptains,
                    )}
                    advancing={competition.advancingPerGroup ?? null}
                    extraQualifiers={competition.extraQualifiers ?? null}
                    matchesLabel={t('draw.result.matchesResolved', {
                      resolved: group.matchesResolved,
                      total: group.matchesTotal,
                    })}
                  />
                ))}
              </div>

              {/* Terminarz fazy grupowej — klik prowadzi do wpisania wyniku */}
              <h2 className="text-lg font-bold text-fg-strong">
                {t('draw.result.groupMatchesTitle')}
              </h2>
              <div className="grid items-start gap-4 lg:grid-cols-2">
                {groups.map((group) => (
                  <Card key={group.number} padding="md">
                    <CardHeader
                      title={t('draw.result.group', { number: group.number })}
                      className="mb-2"
                    />
                    <div className="flex flex-col divide-y divide-border">
                      {matches
                        .filter((match) => match.groupNumber === group.number)
                        .slice()
                        .sort((a, b) => (a.matchNumber ?? 0) - (b.matchNumber ?? 0))
                        .map((match) => (
                          <button
                            key={match.id}
                            type="button"
                            onClick={() => openMatch(toBracketMatchView(match, shortNames, seeds))}
                            className="-mx-2 rounded-lg px-2 text-left transition-colors hover:bg-surface-hover"
                          >
                            <MatchScoreRow
                              match={toBracketMatchView(match, shortNames, seeds)}
                              showAvatars
                            />
                          </button>
                        ))}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Grupy rozstrzygnięte — prowadzimy organizatora do zakładki finału */}
          {phase === 'ready' && (
            <Alert tone="success" title={t('draw.finalPhase.title')}>
              <span className="flex flex-wrap items-center gap-3">
                <span>{t('draw.finalPhase.ready', { phase: secondPhaseLabel })}</span>
                <Button
                  size="sm"
                  trailingIcon={<ArrowRight className="size-4" />}
                  onClick={() => void navigate(`/turnieje/${competitionId}/faza-finalowa`)}
                >
                  {t('draw.finalPhase.goTo')}
                </Button>
              </span>
            </Alert>
          )}
        </>
      )}

      {/* GROUP_PLAYOFF — zakładka „Faza finałowa": awans, drabinka lub grupa finałowa */}
      {kind === 'groups' && finalPhase && (
        <>
          {matchesPending ? (
            <Skeleton className="h-80 w-full" />
          ) : (
            <>
              {phase === 'waiting' && (
                <Alert tone="info" title={t('draw.finalPhase.title')}>
                  {t('draw.finalPhase.waiting', { phase: secondPhaseLabel, count: unresolved })}
                </Alert>
              )}

              {phase === 'ready' && (
                <Alert tone="success" title={t('draw.finalPhase.title')}>
                  <span className="flex flex-wrap items-center gap-3">
                    <span>
                      {t('draw.finalPhase.ready', { phase: secondPhaseLabel })}{' '}
                      {competition.secondPhaseType === 'CLASSIFICATION_GROUPS'
                        ? t('draw.finalPhase.classification')
                        : t('draw.finalPhase.advancing', {
                            advancing: competition.advancingPerGroup ?? 1,
                          })}
                    </span>
                    <Button
                      size="sm"
                      loading={advanceMutation.isPending || advanceSuspense.active}
                      trailingIcon={<Shuffle className="size-4" />}
                      onClick={handleAdvance}
                    >
                      {t('draw.finalPhase.submit')}
                    </Button>
                  </span>
                </Alert>
              )}

              {/* Nakładka losowania fazy finałowej — przykrywa odświeżającą się
                  drabinkę, dopóki pasek nie dojedzie do końca. */}
              {advanceSuspense.active && (
                <DrawSuspenseOverlay
                  progress={advanceSuspense.progress}
                  finalCaption={
                    competition.secondPhaseType === 'FINAL_GROUP'
                      ? t('draw.progress.finalizeGroups')
                      : t('draw.progress.finalizeBracket')
                  }
                />
              )}

              {phase === 'drawn' &&
                competition.bracketSize !== null &&
                bracketSection(t('draw.result.bracketTitle'))}

              {phase === 'drawn' &&
                competition.secondPhaseType !== 'CLASSIFICATION_GROUPS' &&
                finalGroup && (
                  <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,30rem)_minmax(0,1fr)]">
                    <GroupStandingsCard
                      title={t('draw.result.finalGroup')}
                      rows={buildGroupTable(0, finalGroup.members, standings, fullNames, null)}
                      matchesLabel={t('draw.result.matchesResolved', {
                        resolved: finalGroup.matchesResolved,
                        total: finalGroup.matchesTotal,
                      })}
                    />
                    <div className="grid items-start gap-4 lg:grid-cols-2">
                      {finalGroupRounds.map((round) => (
                        <Card key={round.name} padding="md">
                          <CardHeader title={round.label} className="mb-2" />
                          <div className="flex flex-col divide-y divide-border">
                            {round.matches.map((match) => (
                              <button
                                key={match.id}
                                type="button"
                                onClick={() => openMatch(match)}
                                className="-mx-2 rounded-lg px-2 text-left transition-colors hover:bg-surface-hover"
                              >
                                <MatchScoreRow match={match} showAvatars />
                              </button>
                            ))}
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

              {/* Grupy klasyfikacyjne: podium z grupy 1. miejsc, niżej kolejne miejsca */}
              {phase === 'drawn' &&
                competition.secondPhaseType === 'CLASSIFICATION_GROUPS' &&
                classificationGroups.map((group) => (
                  <div
                    key={group.groupNumber}
                    className="grid items-start gap-4 xl:grid-cols-[minmax(0,30rem)_minmax(0,1fr)]"
                  >
                    <GroupStandingsCard
                      title={
                        group.rank === 1
                          ? t('draw.result.podiumGroup')
                          : t('draw.result.classificationGroup', { rank: group.rank })
                      }
                      rows={buildGroupTable(
                        group.groupNumber,
                        group.view.members,
                        standings,
                        fullNames,
                        null,
                      )}
                      matchesLabel={t('draw.result.matchesResolved', {
                        resolved: group.view.matchesResolved,
                        total: group.view.matchesTotal,
                      })}
                    />
                    <div className="grid items-start gap-4 lg:grid-cols-2">
                      {roundRobinRounds(matches, group.groupNumber, shortNames, seeds).map(
                        (round) => (
                          <Card key={`${group.groupNumber}-${round.name}`} padding="md">
                            <CardHeader title={round.label} className="mb-2" />
                            <div className="flex flex-col divide-y divide-border">
                              {round.matches.map((match) => (
                                <button
                                  key={match.id}
                                  type="button"
                                  onClick={() => openMatch(match)}
                                  className="-mx-2 rounded-lg px-2 text-left transition-colors hover:bg-surface-hover"
                                >
                                  <MatchScoreRow match={match} showAvatars />
                                </button>
                              ))}
                            </div>
                          </Card>
                        ),
                      )}
                    </div>
                  </div>
                ))}
            </>
          )}
        </>
      )}
    </div>
  );
}
